import { useState } from "react";
import styles from "./RoomMappingTab.module.css";
import SearchableSelect from "../../../BaseComponents/SearchableSelect/SearchableSelect";
import { Search, Download, RefreshCw, Edit2, Trash2, Building2, Bed, Users, Filter, Link2 } from "lucide-react";
import AddMappingModal from "./AddMappingModal";

// Mock data
const MOCK_MAPPINGS = [
  {
    id: 1,
    hotel: "Burj Al Arab Jumeirah",
    hotelId: 1,
    roomType: "Deluxe Suite",
    roomTypeCode: "DLX",
    bedType: "King",
    maxOccupancy: 2,
    roomSize: 170,
    apiSupplier: "Amadeus GDS",
    apiRoomCode: "KDLX-NS",
    status: "active"
  },
  {
    id: 2,
    hotel: "Burj Al Arab Jumeirah",
    hotelId: 1,
    roomType: "Panoramic Suite",
    roomTypeCode: "PAN",
    bedType: "King",
    maxOccupancy: 3,
    roomSize: 200,
    apiSupplier: "Amadeus GDS",
    apiRoomCode: "KPAN-NS",
    status: "active"
  },
  {
    id: 3,
    hotel: "The Ritz-Carlton",
    hotelId: 2,
    roomType: "Executive Suite",
    roomTypeCode: "EXE",
    bedType: "Queen",
    maxOccupancy: 2,
    roomSize: 85,
    apiSupplier: "Sabre",
    apiRoomCode: "QEXE",
    status: "active"
  },
  {
    id: 4,
    hotel: "Marina Bay Sands",
    hotelId: 3,
    roomType: "Premier Room",
    roomTypeCode: "PRM",
    bedType: "Twin",
    maxOccupancy: 2,
    roomSize: 42,
    apiSupplier: "Booking.com",
    apiRoomCode: "TWN-PRM",
    status: "active"
  },
  {
    id: 5,
    hotel: "Marina Bay Sands",
    hotelId: 3,
    roomType: "Club Room",
    roomTypeCode: "CLB",
    bedType: "King",
    maxOccupancy: 2,
    roomSize: 42,
    apiSupplier: "Booking.com",
    apiRoomCode: "KNG-CLB",
    status: "inactive"
  }
];

const RoomMappingTab = () => {
  const [mappings, setMappings] = useState(MOCK_MAPPINGS);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hotelFilter, setHotelFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  // Get unique hotels and suppliers for filters
  const uniqueHotels = [...new Set(mappings.map(m => m.hotel))].sort();
  const uniqueSuppliers = [...new Set(mappings.map(m => m.apiSupplier))].sort();

  // Filter mappings
  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = 
      mapping.hotel.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.roomType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.roomTypeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.apiRoomCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || mapping.status === statusFilter;
    const matchesHotel = hotelFilter === "all" || mapping.hotel === hotelFilter;
    const matchesSupplier = supplierFilter === "all" || mapping.apiSupplier === supplierFilter;
    
    return matchesSearch && matchesStatus && matchesHotel && matchesSupplier;
  });

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setShowAddModal(true);
  };

  const handleDelete = (mappingId) => {
    if (window.confirm("Are you sure you want to delete this mapping?")) {
      setMappings(prev => prev.filter(m => m.id !== mappingId));
      console.log("DELETE MAPPING:", mappingId);
    }
  };

  const handleSaveMapping = (mappingData) => {
    if (editingMapping) {
      setMappings(prev =>
        prev.map(m => (m.id === editingMapping.id ? { ...m, ...mappingData } : m))
      );
      console.log("UPDATE MAPPING:", mappingData);
    } else {
      const newMapping = {
        id: Math.max(...mappings.map(m => m.id)) + 1,
        ...mappingData
      };
      setMappings(prev => [...prev, newMapping]);
      console.log("CREATE MAPPING:", newMapping);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingMapping(null);
  };

  const handleRefresh = () => {
    console.log("REFRESH MAPPINGS DATA");
  };

  const handleExport = () => {
    console.log("EXPORT MAPPINGS DATA");
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setHotelFilter("all");
    setSupplierFilter("all");
    setSearchTerm("");
  };

  const activeFiltersCount = 
    (statusFilter !== "all" ? 1 : 0) +
    (hotelFilter !== "all" ? 1 : 0) +
    (supplierFilter !== "all" ? 1 : 0);

  return (
    <div className={styles.tabContainer}>
      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Room Mapping Notice:</strong> Room mappings enable automatic inventory sync between your system and API suppliers. Each mapping must have unique room type code and corresponding API room code.
        </div>
      </div>

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          + Add Room Mapping
        </button>
      </div>

      {/* Filters & Actions */}
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by hotel, room type, or room code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <SearchableSelect
            width={200}
            placeholder="All Hotels"
            searchPlaceholder="Search hotel..."
            value={hotelFilter}
            options={[{ label: "All Hotels", value: "all" }, ...uniqueHotels.map((h) => ({ label: h, value: h }))]}
            onChange={setHotelFilter}
          />

          <SearchableSelect
            width={180}
            placeholder="All Suppliers"
            searchPlaceholder="Search supplier..."
            value={supplierFilter}
            options={[{ label: "All Suppliers", value: "all" }, ...uniqueSuppliers.map((sup) => ({ label: sup, value: sup }))]}
            onChange={setSupplierFilter}
          />

          <SearchableSelect
            width={150}
            searchable={false}
            placeholder="All Status"
            value={statusFilter}
            options={[{ label: "All Status", value: "all" }, { label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }]}
            onChange={setStatusFilter}
          />

          {activeFiltersCount > 0 && (
            <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
              <Filter size={16} />
              Clear ({activeFiltersCount})
            </button>
          )}

          <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>

          <button className={styles.iconBtn} onClick={handleExport} title="Export">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className={styles.resultsCount}>
        Showing {filteredMappings.length} of {mappings.length} mappings
        {activeFiltersCount > 0 && <span className={styles.filterIndicator}> (filtered)</span>}
      </div>

      {/* Mappings Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>HOTEL</th>
              <th>ROOM TYPE</th>
              <th>ROOM DETAILS</th>
              <th>API MAPPING</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredMappings.length === 0 ? (
              <tr>
                <td colSpan="6" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>🔗</div>
                    <p>No mappings found</p>
                    <span>Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredMappings.map(mapping => (
                <tr key={mapping.id}>
                  <td>
                    <div className={styles.hotelCell}>
                      <div className={styles.hotelIcon}>
                        <Building2 size={16} />
                      </div>
                      <span>{mapping.hotel}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.roomTypeCell}>
                      <strong>{mapping.roomType}</strong>
                      <span className={styles.roomCode}>{mapping.roomTypeCode}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.roomDetails}>
                      <div className={styles.detailRow}>
                        <Bed size={14} />
                        <span>{mapping.bedType} Bed</span>
                      </div>
                      <div className={styles.detailRow}>
                        <Users size={14} />
                        <span>Max {mapping.maxOccupancy} guests</span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.sizeTag}>{mapping.roomSize} sqm</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.apiMapping}>
                      <div className={styles.mappingHeader}>
                        <Link2 size={14} className={styles.linkIcon} />
                        <span className={styles.supplierName}>{mapping.apiSupplier}</span>
                      </div>
                      <span className={styles.apiCode}>{mapping.apiRoomCode}</span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        mapping.status === "active"
                          ? styles.statusActive
                          : styles.statusInactive
                      }`}
                    >
                      {mapping.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.editBtn}
                        onClick={() => handleEdit(mapping)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => handleDelete(mapping.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <AddMappingModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSaveMapping}
        mapping={editingMapping}
      />
    </div>
  );
};

export default RoomMappingTab;